import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _ from 'lodash/';
import classNames from 'classnames';

import Disclosure, { disclosureTypes } from '../disclosure/disclosure';

import {
  subtitleInput,
  stringSelectSpec,
  stringInputSpec,
  stringTextAreaSpec,
} from '../../lib/editing-configs';
import { getLayoutByYear, getLayoutByYearConfig } from './lib/layout-selector';
import LegacyLayout from './legacy-layout/legacy-layout';
import HeroLayoutBOA2022 from './layout/2022/layout';
import BackgroundColor from '../background-color/background-color';
import getTextColor from '../../lib/text-color-config';

import './awards-hero.less';

const renderHero = (props) => {
  switch (props.awardsYear) {
    case '2022':
      return <HeroLayoutBOA2022 {...props} />;
    default:
      return <LegacyLayout {...props} />;
  }
};

const AwardsHero = (props) => {
  const {
    backgroundSize,
    backgroundSizeMobile,
    backgroundImageSrcDesktop,
    backgroundImageSrcTablet,
    backgroundImageSrcMobile,
    bgOptions,
    heroType2021,
    awardsHeroType,
    disclosureType,
    size,
    selectTopLeftContent,
    awardsYear,
  } = props;

  const heroImageSrcMap = {
    lg: backgroundImageSrcDesktop,
    md: backgroundImageSrcTablet,
    sm: backgroundImageSrcMobile,
  };

  // align items using flexbox align-center to vertically align hero content if no advertiserDisclosure or awardsLogo is present
  const mobileFlexboxRule =
    disclosureType !== 'none' || selectTopLeftContent === 'awardsLogo'
      ? 'flex-start'
      : 'center';

  const heroContentFlexboxRule = {
    lg: 'center',
    md: 'center',
    sm: mobileFlexboxRule,
  };

  const layout = getLayoutByYear(awardsYear, {
    type: awardsHeroType,
    version: heroType2021,
  });

  return (
    <div
      className={classNames(['awards-hero__background-wrapper', `${layout}`])}
    >
      <BackgroundColor bgOptions={bgOptions}>
        {(backgroundColor) => (
          <div
            className={classNames(['awards-hero', `${layout}`])}
            style={{
              backgroundColor,
              backgroundImage: size && `url(${heroImageSrcMap[size]})`,
              backgroundSize:
                size === 'sm' && backgroundSizeMobile
                  ? `${backgroundSizeMobile}`
                  : `${backgroundSize}`,
              alignItems: `${heroContentFlexboxRule[size]}`,
            }}
          >
            {renderHero({ ...props, layout })}
          </div>
        )}
      </BackgroundColor>
    </div>
  );
};

AwardsHero.propTypes = {
  heroType2021: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  subtitleMobile: PropTypes.string,
  subtitleSize: PropTypes.oneOf(['2', '3']),
  backgroundIcon: PropTypes.string,
  backgroundIconMobile: PropTypes.string,
  animatedIcon: PropTypes.string,
  backgroundSize: PropTypes.oneOf(['contain', 'cover', 'auto']),
  backgroundSizeMobile: PropTypes.oneOf(['contain', 'cover', 'auto']),
  backgroundImageSrcDesktop: PropTypes.string,
  backgroundImageSrcTablet: PropTypes.string,
  backgroundImageSrcMobile: PropTypes.string,
  awardsHeroType: PropTypes.oneOf([
    '2021LP1',
    '2021LP2',
    '2022LP1',
    '2022LP2',
    '2022MS',
  ]),
  disclosureType: PropTypes.oneOf([...disclosureTypes, 'none']),
  disclosureOnBackground: PropTypes.bool,
  preheader: PropTypes.string,
  selectTopLeftContent: PropTypes.oneOf(['advertiserDisclosure', 'awardsLogo']),
  size: PropTypes.string,
  ...BackgroundColor.propTypes,
  textColor: PropTypes.string,
  awardsYear: PropTypes.string,
};

AwardsHero.defaultProps = {
  heroType2021: false,
  title: 'Title goes here',
  subtitle:
    'Description (desktop) goes here. Mobile will default to this if no mobile subtitle provided.',
  subtitleSize: '2',
  ...BackgroundColor.defaultProps,
  backgroundIcon: '',
  backgroundIconMobile: '',
  animatedIcon: '',
  backgroundSize: 'contain',
  backgroundSizeMobile: 'contain',
  backgroundImageSrcDesktop: '',
  backgroundImageSrcTablet: '',
  backgroundImageSrcMobile: '',
  preheader: '',
  selectTopLeftContent: 'advertiserDisclosure',
  size: '',
  awardsYear: '2021',
};

AwardsHero.getEditingConfig = (componentDocument) => {
  const disclosureOptions =
    componentDocument?.props?.awardsYear !== '2022'
      ? [
          stringSelectSpec(
            'selectTopLeftContent',
            'Content displayed in top left corner of hero',
            [
              { label: 'Advertiser disclosure', value: 'advertiserDisclosure' },
              { label: 'NW Awards logo', value: 'awardsLogo' },
              { label: 'Nothing', value: 'nothing' },
            ],
            {
              help: 'To output a Best Of Awards badge icon, select "NW Awards logo" from the dropdown. To output an advertiser disclosure, select "Advertiser disclosure" from the dropdown and then the type of disclosure to be output from the "Advertiser disclosure popup" dropdown. To output nothing, select "Advertiser disclosure" from the dropdown and "None" from the "Advertiser disclosure popup" dropdown.',
            }
          ),
        ]
      : [];
  const topLeftContent =
    componentDocument?.props?.selectTopLeftContent === 'advertiserDisclosure'
      ? [...Disclosure.getEditingConfig()]
      : [];

  const imageConfig =
    componentDocument?.props?.awardsYear !== '2022'
      ? [
          stringSelectSpec(
            'backgroundSize',
            'Background Size',
            [
              { label: 'Contain', value: 'contain' },
              { label: 'Cover', value: 'cover' },
              { label: 'Auto', value: 'auto' },
            ],
            {
              help: 'Only applies to Desktop and Tablet',
            }
          ),
          stringSelectSpec(
            'backgroundSizeMobile',
            'Mobile Background Size',
            [
              { label: 'Contain', value: 'contain' },
              { label: 'Cover', value: 'cover' },
              { label: 'Auto', value: 'auto' },
            ],
            {
              help: 'Only applies to Mobile',
            }
          ),
          stringInputSpec(
            'backgroundImageSrcDesktop',
            'Background Image URL (Desktop)'
          ),
          stringInputSpec(
            'backgroundImageSrcTablet',
            'Background Image URL (Tablet)'
          ),
          stringInputSpec(
            'backgroundImageSrcMobile',
            'Background Image URL (Mobile)'
          ),
        ]
      : [];
  const bgColorConfig =
    componentDocument?.props?.awardsYear !== '2022'
      ? [
          ...BackgroundColor.getEditingConfig(componentDocument),
          stringTextAreaSpec('title', 'Title', {
            help: 'For yellow underline styling, surround title text with | like |this|. For outline font styling, surround title text with * like *this*. For 2021 styling, surround title with ^ like ^this^. For 2021 italicized styling, surround title text with a & like $this$. To include a linebreak, insert :br: in the textbox. For Gotham black font surround with a + like +this+',
          }),
        ]
      : [];
  const contentConfig =
    componentDocument?.props?.awardsHeroType !== '2021LP2' &&
    componentDocument?.props?.awardsHeroType !== '2021LP1'
      ? [
          ...getTextColor.getEditingConfig(componentDocument),
          stringTextAreaSpec('title', 'Title', {
            help: 'For yellow underline styling, surround title text with | like |this|. For outline font styling, surround title text with * like *this*. For 2021 styling, surround title with ^ like ^this^. For 2021 italicized styling, surround title text with a & like $this$. To include a linebreak, insert :br: in the textbox. For Gotham black font surround with a + like +this+',
          }),
          stringTextAreaSpec('mobileTitle', 'Mobile Title', {
            help: 'For yellow underline styling, surround title text with | like |this|. For outline font styling, surround title text with * like *this*. For 2021 styling, surround title with ^ like ^this^. For 2021 italicized styling, surround title text with a & like $this$. To include a linebreak, insert :br: in the textbox. For Gotham black font surround with a + like +this+',
          }),
          stringTextAreaSpec('props.subtitleMobile', 'Mobile subtitle'),
          stringSelectSpec(
            'props.subtitleSize',
            'Subtitle Text Size',
            [
              { label: 'Medium (default)', value: '2' },
              { label: 'Large', value: '3' },
            ],
            {
              help: 'Select the size of the subtitle text',
            }
          ),
        ]
      : [];
  const subtitleConfig =
    componentDocument?.props?.awardsHeroType !== '2021LP1'
      ? [subtitleInput]
      : [];
  return [
    stringSelectSpec('awardsYear', 'Awards Year', [
      { label: '2021', value: '2021' },
      { label: '2022', value: '2022' },
      { label: '2020', value: '2020' },
    ]),
    ...getLayoutByYearConfig(componentDocument),
    ...subtitleConfig,
    ...bgColorConfig,
    ...contentConfig,
    ...imageConfig,
    ...disclosureOptions,
    ...topLeftContent,
  ];
};

AwardsHero.displayName = 'AwardsHero';

export default connect(
  createStructuredSelector({
    size: _.property('mediaQuery'),
  })
)(AwardsHero);
